import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { HEADER, GLOBAL_IMAGES } from "../../assets/asset";
import HandleAIQuestions from "./HandleAIQuestions";
import { colors } from "../../assets/components/ai/constants";
import ExitScreenModal from "./ExitScreenModal.jsx";
import { useDispatch, useSelector } from "react-redux";
import { Loader2 } from "lucide-react";
import {
  forceMute,
  rememberCurrentValue,
  restoreOlderValue,
} from "../../store/slice/muteSlice.js";

const readResponseBody = async (response) => {
  const responseText = await response.text();
  if (!responseText) {
    return { responseText: "", responseData: null };
  }

  try {
    return { responseText, responseData: JSON.parse(responseText) };
  } catch (_) {
    return { responseText, responseData: null };
  }
};

const AIHandler = () => {
  const dispatch = useDispatch();
  const [questions, setQuestions] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [submissionError, setSubmissionError] = useState("");
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmExitScreen, setShowConfirmExitScreen] = useState(false);
  const [retryAction, setRetryAction] = useState("");
  const [lastSubmissionPayload, setLastSubmissionPayload] = useState(null);
  const questionsRef = useRef([]);

  const { yearId } = useParams();
  const { token } = useSelector((state) => state.user);
  const navigate = useNavigate();

  useEffect(() => {
    questionsRef.current = questions;
  }, [questions]);

  const fetchAIQuestions = useCallback(
    async ({ showLoader = true, preserveQuestionsOnError = false } = {}) => {
      if (!token || !yearId) {
        setLoadError("Missing session details. Please restart the AI assessment.");
        setLoading(false);
        return;
      }

      if (showLoader) {
        setLoading(true);
        setLoadError("");
      }
      setSubmissionError("");

      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}api/aiNextquestions`,
          {
            method: "POST",
            headers: {
              Authorization: `${process.env.REACT_APP_AUTH_TOKEN_DEV}`,
              "Content-Type": "application/json",
              jwt: token,
            },
            body: JSON.stringify({ YearId: yearId }),
          }
        );

        const { responseText, responseData } = await readResponseBody(response);

        if (!response.ok) {
          console.error("Failed to fetch AI questions", {
            status: response.status,
            responseText,
          });
          throw new Error(
            "Unable to load the next AI assessment question right now. Please try again."
          );
        }

        if (
          responseData?.status === "Fail" &&
          (responseData?.status_code === 304 ||
            responseData?.message === "jwt token is not valid")
        ) {
          navigate("/login", { replace: true });
          return;
        }

        setQuestions(Array.isArray(responseData?.data) ? responseData.data : []);
        setLoadError("");
        setSubmissionError("");
        setRetryAction("");
      } catch (error) {
        if (preserveQuestionsOnError && questionsRef.current.length > 0) {
          setSubmissionError(error.message);
          setRetryAction("fetch");
        } else {
          setLoadError(error.message);
        }
      } finally {
        if (showLoader) setLoading(false);
      }
    },
    [token, yearId, navigate]
  );

  const handleQuestionsComplete = useCallback(
    async (payload) => {
      setLastSubmissionPayload(payload);
      setIsSubmitting(true);
      setSubmissionError("");
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}api/aiNextquestionAnswearSubmmit`,
          {
            method: "POST",
            headers: {
              Authorization: `${process.env.REACT_APP_AUTH_TOKEN_DEV}`,
              "Content-Type": "application/json",
              jwt: token,
            },
            body: JSON.stringify(payload),
          }
        );

        const { responseText, responseData } = await readResponseBody(response);

        if (!response.ok) {
          console.error("Failed to submit AI answers", {
            status: response.status,
            responseText,
            payload,
          });
          setRetryAction("submit");
          throw new Error("Unable to save this AI answer right now. Please try again.");
        }

        if (
          responseData?.status === "Fail" &&
          (responseData?.status_code === 304 ||
            responseData?.message === "jwt token is not valid")
        ) {
          navigate("/login", { replace: true });
          return;
        }

        setRetryAction("");
        setLastSubmissionPayload(null);
        await fetchAIQuestions({
          showLoader: false,
          preserveQuestionsOnError: true,
        });
      } catch (error) {
        setSubmissionError(error.message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [token, navigate, fetchAIQuestions]
  );

  const handleRetry = useCallback(() => {
    if (retryAction === "submit" && lastSubmissionPayload) {
      handleQuestionsComplete(lastSubmissionPayload);
      return;
    }

    fetchAIQuestions({
      showLoader: retryAction !== "fetch",
      preserveQuestionsOnError: retryAction === "fetch",
    });
  }, [retryAction, lastSubmissionPayload, fetchAIQuestions, handleQuestionsComplete]);

  useEffect(() => {
    fetchAIQuestions();
  }, [fetchAIQuestions]);

  useEffect(() => {
    dispatch(rememberCurrentValue());
    dispatch(forceMute());

    return () => {
      dispatch(restoreOlderValue());
    };
  }, [dispatch]);

  if (loading && questions.length === 0)
    return (
      <div className="flex items-center justify-center h-screen w-screen bg-white">
        <Loader2 className="h-12 w-12 animate-spin text-gray-600" />
      </div>
    );

  if (loadError && questions.length === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white px-4">
        <div className="w-full max-w-xl rounded-3xl border-2 border-[#0B2B66] bg-[#FFF7E9] p-6 text-center shadow-[0_12px_30px_rgba(11,43,102,0.18)]">
          <h1 className="text-2xl font-extrabold text-[#0B2B66]">
            AI assessment is temporarily unavailable
          </h1>
          <p className="mt-3 text-sm font-semibold text-[#5C4A2A]">
            {loadError}
          </p>
          <button
            type="button"
            onClick={handleRetry}
            className="mt-5 rounded-xl border-2 border-[#0B2B66] bg-gradient-to-r from-[#FFF27A] via-[#FFBE4D] to-[#FF8F1F] px-5 py-2.5 font-bold text-[#0B2B66] shadow-[0_4px_0_#0B2B66]"
          >
            Retry loading questions
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className=" min-h-screen flex flex-col items-center overflow-y-auto  "
      style={{
        backgroundImage: `url(${GLOBAL_IMAGES.aiBG})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        minHeight: "100vh",
      }}
    >
      {showConfirmExitScreen && (
        <ExitScreenModal
          onClose={() => setShowConfirmExitScreen(false)}
          navigate={navigate}
        />
      )}
      <div
        className=" h-[100px] justify-between  flex items-center  w-full "
        style={{ background: "#36363699" }}
      >
        <img src={HEADER.AI} alt="AI" className="w-20  h-15" />
        <div className=" ">
          <button
            onClick={() => setShowConfirmExitScreen(true)}
            className=" text-white px-12 mr-4 font-mali text-xl font-bold py-2 rounded-lg hover:bg-red-700"
            style={{ background: colors.linearGradient }}
          >
            Exit
          </button>
        </div>
      </div>
      <div className="relative flex w-full flex-1 flex-col items-center my-[5rem] px-4">
        {submissionError && (
          <div className="mb-4 w-full max-w-3xl rounded-2xl border-2 border-[#D9822B] bg-[#FFF4E5] px-5 py-4 text-[#7A470F] shadow-[0_10px_24px_rgba(217,130,43,0.18)]">
            <h2 className="text-lg font-extrabold">Answer sync issue</h2>
            <p className="mt-1 text-sm font-semibold">{submissionError}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleRetry}
                className="rounded-lg border-2 border-[#A8641D] bg-[#D9822B] px-4 py-2 text-sm font-bold text-white"
              >
                Retry
              </button>
              <button
                type="button"
                onClick={() => {
                  setSubmissionError("");
                  setRetryAction("");
                }}
                className="rounded-lg border-2 border-[#0B2B66] bg-white px-4 py-2 text-sm font-bold text-[#0B2B66]"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}
        {questions.length > 0 ? (
          <HandleAIQuestions
            key={`${yearId}-${questions[0]?.pk || "empty"}-${questions.length}`}
            questions={questions}
            yearId={yearId}
            onQuestionsComplete={handleQuestionsComplete}
            colorTheme="#ffffff"
          />
        ) : (
          <div className="rounded-2xl border-2 border-[#0B2B66] bg-white/90 px-6 py-5 text-center shadow-[0_10px_24px_rgba(11,43,102,0.15)]">
            <p className="text-lg font-bold text-[#0B2B66]">
              No AI questions are available right now.
            </p>
            <button
              type="button"
              onClick={handleRetry}
              className="mt-4 rounded-lg border-2 border-[#0B2B66] bg-[#0B2B66] px-4 py-2 text-sm font-bold text-white"
            >
              Refresh
            </button>
          </div>
        )}
        {isSubmitting && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/15 backdrop-blur-[1px]">
            <Loader2 className="h-10 w-10 animate-spin text-white" />
          </div>
        )}
      </div>
    </div>
  );
};

export default AIHandler;
